// Standalone phone app experience. Open to guests (no sign-in wall),
// unlinked from the site and excluded from search, so it can be built
// without touching the web dashboard.
import { createFileRoute } from "@tanstack/react-router";
import { memo, useEffect, useState } from "react";
import { MobileSessionHeader } from "@/components/dashboard/terminal/MobileSessionHeader";
import { SectionAnchor } from "@/components/app/SectionAnchor";
import { SessionContextChart, type Tf } from "@/components/app/SessionContextChart";
import { MacroLensPhoneCard } from "@/components/app/MacroLensPhoneCard";
import { ExecutionRadar, usePublicLeadTrade } from "@/components/app/ExecutionRadar";
import { SessionDnaCard } from "@/components/app/SessionDnaCard";
import { readSessionDna } from "@/components/app/session-dna";
import { RetailLiquidityCard, useRetailSentiment, contrarianShort } from "@/components/app/RetailLiquidityCard";
import { classifySentiment } from "@/lib/retail-sentiment";
import { EconomicRadarCard, shieldTag, shieldText } from "@/components/app/EconomicRadarCard";
import { LiquidityTrapCard, useLiquidityTrap, trapTag } from "@/components/app/LiquidityTrapCard";
import { RiskCockpitCard, useNearestCamarilla } from "@/components/app/RiskCockpitCard";
import { StructurePulseCard, useStructureAlignment } from "@/components/app/StructurePulseCard";
import { useGoldPrice } from "@/hooks/useGoldPrice";
import { useEconomicCalendar } from "@/hooks/useEconomicCalendar";
import { readShield } from "@/lib/macro-shield";
import { useWeekendHold } from "@/hooks/useWeekendHold";
import { headerClockCadence } from "@/lib/weekend-cadence";
import { useMarketStats } from "@/components/dashboard/island/hooks/useMarketStats";
import { supabase } from "@/integrations/supabase/client";
import { resolveClimate } from "@/components/dashboard/command/marketClimate";
import { useLeanMacroLens } from "@/hooks/useLeanMacroLens";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { climateColor } from "@/components/site/MacroLensShowcase";
import { BrandMark } from "@/components/site/BrandMark";
import { NativePushBridge } from "@/components/app/NativePushBridge";
import { useQueryClient } from "@tanstack/react-query";
import { type LeanContext } from "@/lib/lean-api-client";
import { cachedLeanContext, loadLeanContext } from "@/lib/app-context-cache";
import { useAppClock } from "@/components/app/useAppClock";

const ContextChart = memo(SessionContextChart);
const MacroCard = memo(MacroLensPhoneCard);
const RadarCard = memo(ExecutionRadar);
const StructureCard = memo(StructurePulseCard);
const TrapCard = memo(LiquidityTrapCard);
const DnaCard = memo(SessionDnaCard);
const RetailCard = memo(RetailLiquidityCard);

function LiveHeader(props: Omit<React.ComponentProps<typeof MobileSessionHeader>, "now" | "weekend" | "shield"> & { events: React.ComponentProps<typeof EconomicRadarCard>["events"] }) {
  // Weekend rest: the header clock ticks every 10s while gold is closed and
  // shows minutes only, then returns to seconds at the reopen.
  const now = useAppClock(headerClockCadence());
  const weekend = useWeekendHold(1000, now);
  const { events, ...header } = props;
  return <MobileSessionHeader {...header} now={now} weekend={weekend} shield={events ? readShield(events, now) : null} />;
}

const EconomicSection = memo(function EconomicSection({ events }: { events: React.ComponentProps<typeof EconomicRadarCard>["events"] }) {
  const now = useAppClock();
  const shield = events ? readShield(events, now) : null;
  return <>
    <SectionAnchor index="09" title="Economic Radar" status="Shield">
      <span className={`ml-1 border-l border-border/60 pl-1.5 ${shieldText(shield?.state ?? "clear")}`}>{events ? shieldTag(shield?.state ?? "clear") : "--"}</span>
    </SectionAnchor>
    <EconomicRadarCard events={events} shield={shield} now={now} />
  </>;
});

export const Route = createFileRoute("/app")({
  ssr: false,
  head: () => ({
    meta: [
      { name: "viewport", content: "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover" },
      { title: "XAU Pro Academy App" },
      { name: "description", content: "XAU Pro Academy mobile terminal for live XAUUSD session context." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "XAU Pro Academy App" },
      { property: "og:description", content: "Live XAUUSD session context from XAU Pro Academy." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AppExperience,
});

function AppExperience() {
  return <><NativePushBridge /><ContextGate /></>;
}

// One Lean context request seeds every context card before they mount, so the
// first paint needs one network call instead of six. A slow or failed bundle
// releases after 2.5s and each card then reads on its own. When a previous
// bundle is available (in memory after sign-in, or in phone storage after
// Android closed the app in the background), the screen paints immediately
// and the fresh bundle reseeds the cards in the background. Only the very
// first open on a device waits, behind the native launch logo.
function ContextGate() {
  const qc = useQueryClient();
  const [ready, setReady] = useState<{ calendar: LeanContext["calendar"] } | null>(() => {
    const prev = cachedLeanContext();
    if (!prev) return null;
    seedContext(qc, prev);
    return { calendar: prev.calendar };
  });
  useEffect(() => {
    let done = ready != null;
    const hadPrev = done;
    let alive = true;
    const release = (calendar: LeanContext["calendar"]) => { if (!done) { done = true; setReady({ calendar }); } };
    const timer = setTimeout(() => release(null), 2500);
    loadLeanContext().then((c) => {
      if (!alive) return;
      if (done && !hadPrev) return;
      seedContext(qc, c);
      if (hadPrev) { setReady({ calendar: c.calendar }); return; }
      release(c.calendar);
    }).catch(() => {
      // The stored reading painted, but the refresh failed: let each card read on its own.
      if (hadPrev && alive) for (const key of CONTEXT_KEYS) qc.invalidateQueries({ queryKey: key });
      release(null);
    }).finally(() => clearTimeout(timer));
    return () => { alive = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qc]);
  return ready ? <AppHome calendarSeed={ready.calendar} /> : <main className="min-h-screen bg-background" />;
}

const CONTEXT_KEYS = [["market-stats-snapshot"], ["desk-intel"], ["volatility-heatmap"], ["retail-sentiment"], ["app-context-candles", "H1"]] as const;

function seedContext(qc: ReturnType<typeof useQueryClient>, c: LeanContext) {
  const values = [c.stats, c.intel, c.heatmap, c.sentiment, c.candlesH1];
  CONTEXT_KEYS.forEach((key, i) => { if (values[i] != null) qc.setQueryData(key, values[i]); });
}

function AppHome({ calendarSeed }: { calendarSeed: LeanContext["calendar"] }) {
  const now = useAppClock(60_000);
  useEffect(() => {
    // Tell the Android shell the cards have painted, so its launch logo fades only now.
    const id = requestAnimationFrame(() => requestAnimationFrame(() => {
      (window as unknown as { __xauPainted?: boolean }).__xauPainted = true;
    }));
    // Older phones (few cores or little memory) skip live glass blur, which is
    // the costliest effect per scroll frame. Colors and layout stay identical.
    const nav = navigator as Navigator & { deviceMemory?: number };
    const lite = (nav.hardwareConcurrency ?? 8) <= 4 || (nav.deviceMemory ?? 8) <= 3;
    document.documentElement.classList.toggle("app-lite", lite);
    return () => { cancelAnimationFrame(id); document.documentElement.classList.remove("app-lite"); };
  }, []);
  const live = useGoldPrice(60_000, "lean");
  const stats = useMarketStats();
  const weekend = useWeekendHold(60_000, now);
  const snap = stats.snapshot;
  const pct = snap.dailyChangePct;
  const climate = resolveClimate(pct);
  const calendar = useEconomicCalendar(calendarSeed);
  const leadTrade = usePublicLeadTrade(live.price ?? snap.effectivePrice);
  useEffect(() => {
    const target = new URLSearchParams(window.location.search).get("trade");
    if (!target || !/^(otas-|osas-)[A-Za-z0-9._-]+$/.test(target)) return;
    document.querySelector('[aria-label="Execution Radar"]')?.scrollIntoView({ behavior: "smooth" });
  }, []);
  const [tf, setTf] = useState<Tf>("H1");
  const pull = usePullToRefresh();
  const mlScore = useLeanMacroLens().result?.score ?? null;
  const adrConsumed = readSessionDna(stats).consumed;
  const nearestCam = useNearestCamarilla(live.price ?? snap.effectivePrice);
  const retailLong = useRetailSentiment().data?.latest?.longPct;
  const align = useStructureAlignment();
  const trap = useLiquidityTrap(live.price ?? snap.effectivePrice);
  const todayRange = stats.daily.high != null && stats.daily.low != null ? stats.daily.high - stats.daily.low : null;
  const [user, setUser] = useState<{ name: string | null } | null>(null);
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      const u = data.session?.user;
      if (!u) return;
      const full = (u.user_metadata?.full_name ?? u.user_metadata?.name ?? "") as string;
      setUser({ name: full.split(" ")[0] || null });
    });
  }, []);
  const h = new Date(now).getHours();
  const part = h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
  const greeting = user ? (user.name ? `${part}, ${user.name}` : part) : "Welcome, Trader";

  return (
    <main className="app-terminal mx-auto min-h-screen max-w-md bg-background px-4 pt-6 pb-0">
      <div
        ref={pull.wrapRef}
        aria-hidden={!pull.refreshing}
        aria-label={pull.refreshing ? "Refreshing" : undefined}
        role="status"
        className="pointer-events-none fixed inset-x-0 top-[calc(env(safe-area-inset-top)+0.5rem)] z-[60] flex justify-center transition-opacity duration-200"
        style={{ opacity: pull.refreshing ? 1 : 0, willChange: "transform, opacity" }}
      >
        <span
          ref={pull.badgeRef}
          className="grid h-9 w-9 place-items-center rounded-full border border-gold/30 bg-card shadow-lg"
          style={{ transform: "scale(0.6)" }}
        >
          <svg ref={pull.svgRef} viewBox="0 0 24 24" className={`h-5 w-5 ${pull.refreshing ? "animate-spin" : ""}`}>
            <circle cx="12" cy="12" r="9" fill="none" stroke="var(--gold)" strokeOpacity="0.2" strokeWidth="2.5" />
            <circle ref={pull.arcRef} cx="12" cy="12" r="9" fill="none" stroke="var(--gold)" strokeWidth="2.5" strokeLinecap="round"
              strokeDasharray="56.5" strokeDashoffset={pull.refreshing ? 40 : 56.5} />
          </svg>
        </span>
      </div>
      <LiveHeader
        events={calendar.events}
        price={live.price ?? snap.effectivePrice}
        direction={live.direction}
        delta={snap.dailyDelta}
        pct={pct}
        climate={climate}
        openCount={0}
        compactTop="top-0"
        greeting={greeting}
        tier={user ? "Member" : "Guest"}
        stats={{ high: stats.daily.high, low: stats.daily.low, adr: stats.avgDailyRange5d, sessionPos: null, weekOpen: stats.weekly.open }}
        trade={leadTrade}
        sessions
      />
      <SectionAnchor index="01" title="Market Context" status="Timeframe">
        <TfRoller tf={tf} onTf={setTf} />
      </SectionAnchor>
      <ContextChart
        livePrice={live.price ?? snap.effectivePrice}
        pdh={stats.prevDay.high}
        pdl={stats.prevDay.low}
        climate={climate}
        tf={tf}
        onTf={setTf}
        todayRange={todayRange}
      />
      <SectionAnchor index="02" title="MacroLens™ Pulse" status="Score">
        <span className="ml-1 border-l border-border/60 pl-1.5" style={{ color: mlScore != null ? climateColor(mlScore).color : undefined }}>
          {mlScore != null ? `${mlScore > 0 ? "+" : ""}${Math.round(mlScore)}` : "--"}
        </span>
      </SectionAnchor>
      <div className="app-deferred-section"><MacroCard weekend={weekend.closed} /></div>
      <div className="app-live-section"><RadarCard price={live.price ?? snap.effectivePrice} closed={weekend.closed} /></div>
      <div className="app-deferred-section">
      <SectionAnchor index="04" title="Structure" status="Aligned">
        <span className={`ml-1 border-l border-border/60 pl-1.5 ${align.lead === "bull" ? "text-emerald" : align.lead === "bear" ? "text-destructive" : "text-gold"}`}>
          {align.total ? `${align.count}/${align.total}` : "--"}
        </span>
      </SectionAnchor>
      <StructureCard nowMs={now} closed={weekend.closed} />
      </div>
      <div className="app-deferred-section">
      <SectionAnchor index="05" title="Liquidity" status="Pools">
        <span className={`ml-1 border-l border-border/60 pl-1.5 ${trap === "trap-up" || trap === "run-down" ? "text-destructive" : trap === "trap-down" || trap === "run-up" ? "text-emerald" : "text-gold"}`}>{trapTag(trap)}</span>
      </SectionAnchor>
      <TrapCard price={live.price ?? snap.effectivePrice} closed={weekend.closed} />
      </div>
      <div className="app-deferred-section">
      <SectionAnchor index="06" title="Session DNA" status="ADR">
        <span className="ml-1 border-l border-border/60 pl-1.5 text-gold">
          {adrConsumed != null ? `${Math.round(adrConsumed)}%` : "--"}
        </span>
      </SectionAnchor>
      <DnaCard stats={stats} price={live.price ?? snap.effectivePrice} fetchedAt={stats.fetchedAt} dayKey={stats.dayKey} now={now} closed={weekend.closed} />
      </div>
      <div className="app-deferred-section">
      <SectionAnchor index="07" title="Risk Cockpit" status="Nearest">
        <span className="ml-1 border-l border-border/60 pl-1.5 text-gold">{nearestCam ?? "--"}</span>
      </SectionAnchor>
      <RiskCockpitCard price={live.price ?? snap.effectivePrice} />
      </div>
      <div className="app-deferred-section">
      <SectionAnchor index="08" title="Retail Sentiment" status="Contrarian">
        <span className="ml-1 border-l border-border/60 pl-1.5 text-gold">{retailLong == null || !Number.isFinite(retailLong) ? "--" : contrarianShort(classifySentiment(retailLong).label)}</span>
      </SectionAnchor>
      <RetailCard price={live.price ?? snap.effectivePrice} />
      </div>
      <div className="app-deferred-section"><EconomicSection events={calendar.events} /></div>
      <footer className="mt-10 px-5 pb-[calc(env(safe-area-inset-bottom,0px)+28px)]">
        <div className="relative mx-auto flex h-16 w-16 items-center justify-center" aria-hidden>
          <div className="absolute inset-0 rounded-full bg-gold/10 blur-xl" />
          <BrandMark className="relative h-9 w-9 drop-shadow-[0_0_10px_rgba(212,175,55,0.35)]" />
        </div>
        <div className="flex items-center gap-3" aria-hidden>
          <div className="h-px flex-1 bg-gradient-to-r from-transparent to-gold/30" />
          <span className="font-mono text-[13px] uppercase tracking-[0.28em] text-gold/80">XAU Pro Academy</span>
          <div className="h-px flex-1 bg-gradient-to-l from-transparent to-gold/30" />
        </div>
        <p className="mt-4 text-center font-mono text-[8px] uppercase tracking-[0.12em] text-muted-foreground/70">Powered by Orion AI™</p>
        <p className="mt-1 text-center font-mono text-[8px] uppercase tracking-[0.12em] text-muted-foreground/70">Quantitative & Execution Engine</p>
        <p className="mt-1.5 text-center font-mono text-[7px] uppercase tracking-[0.08em] text-muted-foreground/45">For educational and research purposes only. Not financial advice.</p>
        <p className="mt-1.5 text-center font-mono text-[7px] uppercase tracking-[0.08em] text-muted-foreground/45">Proprietary systems of Orion AI Technologies. All rights reserved.</p>
      </footer>
    </main>
  );
}

const TFS = ["M15", "H1", "H4"] as const;

// Single timeframe roller inside the section tag: swipe up or down to step,
// tap to cycle. Keeps the tag the same size as the status pill.
function TfRoller({ tf, onTf }: { tf: Tf; onTf: (t: Tf) => void }) {
  const [startY, setStartY] = useState<number | null>(null);
  const [dir, setDir] = useState(1);
  const i = TFS.indexOf(tf as (typeof TFS)[number]);
  const step = (d: number) => {
    setDir(d);
    onTf(TFS[(i + d + TFS.length) % TFS.length]);
  };
  return (
    <button
      type="button"
      aria-label={`Timeframe ${tf}. Swipe or tap to change`}
      className="relative ml-1 h-[14px] w-8 touch-none overflow-hidden border-l border-border/60 pl-1.5 text-gold"
      onPointerDown={(e) => setStartY(e.clientY)}
      onPointerUp={(e) => {
        if (startY == null) return;
        const dy = e.clientY - startY;
        setStartY(null);
        step(Math.abs(dy) > 8 && dy > 0 ? -1 : 1);
      }}
    >
      <span key={tf} className="absolute inset-y-0 left-1.5 flex items-center" style={{ animation: `tf-roll-${dir > 0 ? "up" : "down"} .28s cubic-bezier(.22,.9,.3,1)` }}>
        {tf}
      </span>
      <style>{`@keyframes tf-roll-up{from{transform:translateY(100%);opacity:0}to{transform:none;opacity:1}}@keyframes tf-roll-down{from{transform:translateY(-100%);opacity:0}to{transform:none;opacity:1}}`}</style>
    </button>
  );
}
